// src/domains/@platform/auth/auth.store.js
//
// Namespaced Vuex module that owns all authentication / identity state.
// Role strings are server-driven (roleCatalog from login / GET /api/auth/me); API enforces access.

import { TokenService } from '@/shared/http/token';
import { hasMinRole as rbacHasMinRole, normalizeRole } from '@/shared/auth/rbac';
import { clearPersistedState } from '@/store/plugins/persistence';
import { AI_FEATURE_IDS, AI_PACKAGE_FEATURE_KEYS, AI_PACKAGE_FLAGS } from '@/shared/constants/packageFeatures';

const getDefaults = () => ({
  token: '',
  userId: '',
  userRole: '',
  aiAdmin: false,
  institutionId: '',
  firstName: '',
  userProfileId: '',
  institutionLogo: '',
  institutionDisplayName: '',
  enabledFeatures: [],
  aiPackageSettings: [],
  /** From server: { roles, legacyAliases, levels } — never trust alone; API enforces RBAC. */
  roleCatalog: null,
});

const normalizeFlagTitle = (title) => String(title || '').trim().toLowerCase();
const normalizeFeatureId = (featureId) => String(featureId || '').trim().toLowerCase();

function findFeatureSettings(aiPackageSettings, featureName) {
  const featureId = AI_FEATURE_IDS[featureName];
  if (!featureId) return null;
  const expected = new Set([
    normalizeFeatureId(featureId),   // canonical display id
    normalizeFeatureId(featureName), // key id
  ]);
  return Array.isArray(aiPackageSettings)
    ? aiPackageSettings.find((f) => expected.has(normalizeFeatureId(f?.featureId)))
    : null;
}

const authStore = {
  namespaced: true,

  /**
   * Keys that the persistence plugin will sync with localStorage.
   * aiAdmin is NOT persisted — server-authoritative only, fetched via /api/auth/me.
   */
  persist: [
    'token',
    'userId',
    'userRole',
    'institutionId',
    'firstName',
    'userProfileId',
    'institutionLogo',
    'institutionDisplayName',
    'enabledFeatures',
    'aiPackageSettings',
    'roleCatalog',
  ],

  state: getDefaults,

  getters: {
    isAuthenticated: (state) => !!state.token,

    /** Returns a *function* so callers can pass the required role at call time. */
    hasMinRole: (state) => (requiredRole) =>
      rbacHasMinRole(state.userRole, requiredRole, state.roleCatalog),

    /** Returns a *function* — checks whether featureName is enabled in aiPackageSettings. */
    isFeatureEnabled: (state) => (featureName) => {
      if (!AI_FEATURE_IDS[featureName]) return false;
      // Migration-safe fail-open: when PKG_AI_001 is missing, backend allows access.
      if (!Array.isArray(state.aiPackageSettings) || state.aiPackageSettings.length === 0) {
        return true;
      }
      const feature = findFeatureSettings(state.aiPackageSettings, featureName);
      return Boolean(feature?.enabled ?? feature?.isEnabled ?? false);
    },

    isFlagEnabled: (state, getters) => (featureName, flagTitle) => {
      if (!flagTitle) return getters.isFeatureEnabled(featureName);

      let effectiveFlagTitle = flagTitle;
      // Role-driven resolution for AI Assistant Bot access:
      // student -> Enable for Learner, others -> Enable for Non Learner.
      if (
        featureName === AI_PACKAGE_FEATURE_KEYS.AI_ASSISTANT_BOT &&
        (flagTitle === AI_PACKAGE_FLAGS.enableForLearner ||
          flagTitle === AI_PACKAGE_FLAGS.enableForNonLearner)
      ) {
        effectiveFlagTitle = getters.isStudent
          ? AI_PACKAGE_FLAGS.enableForLearner
          : AI_PACKAGE_FLAGS.enableForNonLearner;
      }

      if (!AI_FEATURE_IDS[featureName]) return false;
      // Migration-safe fail-open for missing PKG_AI_001 on tenant.
      if (!Array.isArray(state.aiPackageSettings) || state.aiPackageSettings.length === 0) {
        return true;
      }
      const feature = findFeatureSettings(state.aiPackageSettings, featureName);
      if (!feature) return false;
      const featureEnabled = Boolean(feature?.enabled ?? feature?.isEnabled ?? false);
      if (!featureEnabled) return false;
      const normalizedExpectedTitle = normalizeFlagTitle(effectiveFlagTitle);
      const flag = Array.isArray(feature.flags)
        ? feature.flags.find((f) => normalizeFlagTitle(f?.title) === normalizedExpectedTitle)
        : null;
      return Boolean(flag?.enabled ?? flag?.isEnabled ?? false);
    },

    /** Normalized student check — resolves wire value from server catalog. */
    isStudent: (state) => {
      const wire = state.roleCatalog?.keyMap?.STUDENT;
      return wire ? normalizeRole(state.userRole, state.roleCatalog) === wire : false;
    },

    isAdmin: (state) => state.aiAdmin === true,
  },

  mutations: {
    SET_TOKEN(state, value) { state.token = value; },
    SET_USER_ID(state, value) { state.userId = value; },
    SET_USER_ROLE(state, value) { state.userRole = value; },
    SET_AI_ADMIN(state, value) { state.aiAdmin = value; },
    SET_INSTITUTION_ID(state, value) { state.institutionId = value; },
    SET_FIRST_NAME(state, value) { state.firstName = value; },
    SET_USER_PROFILE_ID(state, value) { state.userProfileId = value; },
    SET_INSTITUTION_LOGO(state, value) { state.institutionLogo = value; },
    SET_INSTITUTION_DISPLAY_NAME(state, value) { state.institutionDisplayName = value; },
    SET_ENABLED_FEATURES(state, value) { state.enabledFeatures = value; },
    SET_AI_PACKAGE_SETTINGS(state, value) { state.aiPackageSettings = Array.isArray(value) ? value : []; },
    SET_ROLE_CATALOG(state, value) { state.roleCatalog = value; },

    /** Bulk-load from the persistence plugin during hydration. */
    HYDRATE(state, payload) {
      for (const key of Object.keys(payload)) {
        if (key in state) {
          state[key] = payload[key];
        }
      }
    },

    /** Return every key to its default value. */
    RESET(state) {
      Object.assign(state, getDefaults());
    },
  },

  actions: {
    logout({ commit }) {
      commit('resetStore', null, { root: true });
      commit('RESET');
      clearPersistedState('auth');
      clearPersistedState('insights');
      TokenService.removeToken();
      try {
        localStorage.removeItem('auth.aiAdmin');
        localStorage.removeItem('aiAdmin');
      } catch { /* noop */ }
    },
  },
};

export default authStore;
